import { isNil } from 'lodash-es'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import { dateFormatters, maybeParseDate } from '@/utils/date-util'
import { asNumber, numberToLocaleString } from '@/utils/number-util'

/**
 * The kind of a computed value, which decides its color.
 */
export type PreviewValueTone =
  'String' | 'Number' | 'Boolean' | 'Date' | 'Other'

/**
 * A computed value, formatted for its type.
 */
export interface PreviewValue {
  tone: PreviewValueTone
  text: string
}

/**
 * Formats a value the engine computed for a legend symbol of the type. A
 * missing value is empty, whatever the type.
 *
 * @param type
 * @param value
 */
export function formatPreviewValue(
  type: RynoType,
  value: unknown,
): PreviewValue {
  if (Types.isStringType(type)) {
    return { tone: 'String', text: toText(value) }
  }
  if (Types.isNumberType(type)) {
    return {
      tone: 'Number',
      text: isNil(value) ? '' : numberToLocaleString(asNumber(value)),
    }
  }
  if (Types.isBoolType(type)) {
    return { tone: 'Boolean', text: isNil(value) ? '' : value ? 'Yes' : 'No' }
  }
  if (Types.equalsType(type, Types.date)) {
    if (isNil(value)) {
      return { tone: 'Date', text: '' }
    }
    const date =
      value instanceof Date
        ? value
        : typeof value === 'string'
          ? maybeParseDate(value)
          : undefined
    if (date) {
      return {
        tone: 'Date',
        text: dateFormatters.dateLongWithTime.format(date),
      }
    }
  }
  return { tone: 'Other', text: toText(value) }
}

function toText(value: unknown) {
  if (isNil(value)) {
    return ''
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}
